/**
 * Oppslag langs en rute: "hvor er jeg, hvor høyt, og er jeg i tunnel?"
 *
 * Rutene er allerede delt i segmenter med `structure` (surface/bridge/tunnel)
 * av `npm run routes`, og høydeprofilen er bakt ut av terrengflisene av
 * `scripts/bake-route-elevation.py`. Denne modulen syr de to sammen til én
 * spørring på avstand, slik at en animasjon senere slipper å vite noe om
 * hvordan rutene er stykket opp.
 *
 * Segmentene er verifisert sammenhengende – største skjøt mellom to nabo-
 * segmenter er 0,00 m for begge ruter – så vi kan tre dem på én snor og
 * droppe det duplikate første punktet i hvert påfølgende segment.
 *
 * Modulen er bevisst fri for MapLibre- og data-import, så den kan testes
 * direkte i node (se tests/route-sampler.test.js).
 */

const R = 6371008.8;

function haversine(a, b) {
  const p1 = (a[1] * Math.PI) / 180;
  const p2 = (b[1] * Math.PI) / 180;
  const dp = p2 - p1;
  const dl = ((b[0] - a[0]) * Math.PI) / 180;
  const x =
    Math.sin(dp / 2) ** 2 +
    Math.cos(p1) * Math.cos(p2) * Math.sin(dl / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(x));
}

/** Kompasskurs i grader (0 = nord, 90 = øst) fra a til b. */
function bearing(a, b) {
  const p1 = (a[1] * Math.PI) / 180;
  const p2 = (b[1] * Math.PI) / 180;
  const dl = ((b[0] - a[0]) * Math.PI) / 180;
  const y = Math.sin(dl) * Math.cos(p2);
  const x = Math.cos(p1) * Math.sin(p2) - Math.sin(p1) * Math.cos(p2) * Math.cos(dl);
  return ((Math.atan2(y, x) * 180) / Math.PI + 360) % 360;
}

/**
 * @param {object} featureCollection  route-<mode>-processed.geojson
 * @param {number[][]} [profiles]     route-elevation.json[mode] – én array
 *   per feature, samme rekkefølge og lengde. Utelates den, blir `elevation`
 *   null og kalleren må falle tilbake på terrengoppslag.
 */
export function createRouteSampler(featureCollection, profiles) {
  const feats = featureCollection.features;
  const featureStart = [];   // avstand fra rutestart til hvert segments start
  const lng = [];
  const lat = [];
  const elev = [];
  const meta = []; // per punkt: hvilken feature det kom fra
  const dist = [];

  feats.forEach((f, fi) => {
    const coords = f.geometry.coordinates;
    const prof = profiles ? profiles[fi] : null;
    if (prof && prof.length !== coords.length) {
      throw new Error(
        `route-elevation.json er ute av sync med segment ${fi}: ` +
          `${prof.length} høyder mot ${coords.length} punkter. Kjør npm run routes.`,
      );
    }
    // Første punkt i et påfølgende segment er identisk med forrige segments
    // siste – ta det bare med én gang, ellers får vi et nullengde-ledd.
    const start = fi === 0 ? 0 : 1;
    for (let i = start; i < coords.length; i++) {
      lng.push(coords[i][0]);
      lat.push(coords[i][1]);
      elev.push(prof ? prof[i] : null);
      meta.push(fi);
    }
  });

  dist.push(0);
  for (let i = 1; i < lng.length; i++) {
    dist.push(dist[i - 1] + haversine([lng[i - 1], lat[i - 1]], [lng[i], lat[i]]));
  }
  const length = dist[dist.length - 1];

  // Hvor langt uti ruta hvert segment begynner – trengs for å klippe ruta
  // ved en gitt avstand uten å regne om geometrien.
  //
  // Hvert segment DELER sitt siste punkt med neste segments første, så neste
  // start ligger på `lengde - 1` punkter fram – for alle segmenter, ikke bare
  // det første. Trakk man bare fra på fi===0, skled indeksen ett hakk per
  // segment, og med 39 segmenter havnet det siste utenfor rutas lengde.
  for (let fi = 0, i = 0; fi < feats.length; fi++) {
    featureStart.push(dist[i]);
    i += feats[fi].geometry.coordinates.length - 1;
  }

  function props(fi) {
    const p = feats[fi].properties;
    return {
      structure: p.structure ?? 'surface',
      roadClass: p.road_class ?? null,
      name: p.name ?? null,
      layer: p.layer ?? 0,
    };
  }

  /** Siste indeks i med dist[i] <= d. */
  function seek(d) {
    let lo = 0;
    let hi = dist.length - 1;
    while (lo < hi) {
      const mid = (lo + hi + 1) >> 1;
      if (dist[mid] <= d) lo = mid;
      else hi = mid - 1;
    }
    return lo;
  }

  return {
    length,
    pointCount: lng.length,

    /**
     * @param {number} d meter fra rutestart (klemmes til [0, length])
     * @returns {{lngLat:[number,number], elevation:number|null, structure:string,
     *            roadClass:string|null, name:string|null, bearing:number,
     *            distance:number, progress:number}}
     */
    at(d) {
      const clamped = Math.min(Math.max(d, 0), length);
      const i = seek(clamped);
      const j = Math.min(i + 1, lng.length - 1);
      const span = dist[j] - dist[i];
      const t = span > 0 ? (clamped - dist[i]) / span : 0;

      const lo = [lng[i], lat[i]];
      const hi = [lng[j], lat[j]];
      const e0 = elev[i];
      const e1 = elev[j];

      return {
        lngLat: [lo[0] + (hi[0] - lo[0]) * t, lo[1] + (hi[1] - lo[1]) * t],
        elevation: e0 == null || e1 == null ? null : e0 + (e1 - e0) * t,
        // Struktur tas fra leddet vi står PÅ, ikke fra nærmeste punkt: står
        // du 1 m inn i en tunnel er du i tunnelen, ikke ved portalen.
        //
        // Derfor meta[j], ikke meta[i]: hvert segment deler sitt FØRSTE punkt
        // med forrige segment, så det punktet bærer forrige segments meta.
        // Leddet mellom i og j tilhører den som eier det ØVRE punktet. Et
        // segment på bare to punkter (f.eks. en 22 m bru) bidrar etter dedup
        // med ett eneste punkt – sitt siste – og ble ellers aldri rapportert.
        ...props(meta[j]),
        bearing: span > 0 ? bearing(lo, hi) : 0,
        distance: clamped,
        progress: length > 0 ? clamped / length : 0,
      };
    },

    /** Som at(), men med t i [0, 1] langs hele ruta. */
    atProgress(t) {
      return this.at(t * length);
    },

    /**
     * Ruta klippet ved `d` meter, som en FeatureCollection med SAMME
     * egenskaper per segment. Brukes til å «tegne opp» ruta: strukturen
     * beholdes, så tunnelsegmenter fortsatt filtreres til det stiplede
     * laget mens de vokser fram.
     *
     * @param {number} d meter fra start
     */
    sliceTo(d) {
      const cut = Math.min(Math.max(d, 0), length);
      const features = [];
      for (let fi = 0; fi < feats.length; fi++) {
        const coords = feats[fi].geometry.coordinates;
        const start = featureStart[fi];
        if (cut <= start) break;              // ikke nådd hit ennå
        const end = fi + 1 < feats.length ? featureStart[fi + 1] : length;
        if (cut >= end) {                      // helt ferdig tegnet
          features.push(feats[fi]);
          continue;
        }
        // Segmentet er delvis tegnet: finn punktet og klipp der.
        const local = [0];
        for (let i = 1; i < coords.length; i++) {
          local.push(local[i - 1] + haversine(coords[i - 1], coords[i]));
        }
        const want = cut - start;
        let k = 1;
        while (k < local.length && local[k] < want) k++;
        const span = local[k] - local[k - 1];
        const t = span > 0 ? (want - local[k - 1]) / span : 0;
        const a = coords[k - 1], b = coords[Math.min(k, coords.length - 1)];
        const partial = coords.slice(0, k).concat([[
          a[0] + (b[0] - a[0]) * t,
          a[1] + (b[1] - a[1]) * t,
        ]]);
        if (partial.length >= 2) {
          features.push({ ...feats[fi], geometry: { type: 'LineString', coordinates: partial } });
        }
        break;
      }
      return { type: 'FeatureCollection', features };
    },
  };
}
